import React, { useEffect, useState } from 'react';
import { Form, FormInstance, Input } from 'antd';

// Custom input component for Form.Item to properly trigger onChange
interface PackageSizeInputProps {
  value?: PackageSizeFormValues;
  onChange?: (value: PackageSizeFormValues) => void;
  form: FormInstance;
  onPackageSizeChange: (size: string) => void;
  packageSizeOptions: PackageSizeOption[];
  selectedSize: PackageSizeFormValues;
  setSelectedSize: (size: PackageSizeFormValues) => void;
  t: (key: string) => string;
}

const PackageSizeInput: React.FC<PackageSizeInputProps> = ({
  value,
  onChange,
  form,
  onPackageSizeChange,
  packageSizeOptions,
  selectedSize,
  setSelectedSize,
  t,
}) => {
  const handleSelect = (size: PackageSizeFormValues) => {
    setSelectedSize(size);
    if (onChange) {
      onChange(size);
    }
    onPackageSizeChange(size);
  };

  return (
    <div className='grid grid-cols-2 gap-2'>
      {packageSizeOptions.map((option) => (
        <PackageSize
          key={option.key}
          packageSizeKey={option.key}
          title={option.title}
          description={t(option.description)}
          icon={option.icon}
          selectedValue={value || selectedSize}
          onSelect={handleSelect}
          form={form}
        />
      ))}
    </div>
  );
};

import { Card } from '../Card';
import { PackageSize } from '../PackageSize';
import { Heading } from '../Heading';
import { useTranslation } from 'react-i18next';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { BoxLargeIcon, BoxMediumIcon, BoxSmallIcon, BoxXLargeIcon } from '../Icons';
import { OrderOption, OrderOptionEnum } from '@/types';
import FormCheckbox from './FormCheckbox';
import FragileIcon from '../Icons/fragileIcon';
import { Snowflake } from 'lucide-react';

export type PackageSizeFormValues = 'XS' | 'S' | 'M' | 'L' | 'XL';
interface PackageSizeOption {
  key: PackageSizeFormValues;
  description: string;
  title: string;
  icon: React.FC; // Icon component for size
}

export enum PackageSizeEnum {
  XS = 'extra_small',
  S = 'small',
  M = 'medium',
  L = 'large',
  XL = 'x_large',
}

export const packageSizeOptions: PackageSizeOption[] = [
  { title: 'extra_small', key: 'XS', description: 'up_to_half_kg', icon: BoxSmallIcon },
  { title: 'small', key: 'S', description: 'up_to_one_kg', icon: BoxSmallIcon },
  { title: 'medium', key: 'M', description: 'up_to_three_kg', icon: BoxMediumIcon },
  { title: 'large', key: 'L', description: 'up_to_five_kg', icon: BoxLargeIcon },
  { title: 'x_large', key: 'XL', description: 'up_to_*_kg', icon: BoxLargeIcon },
];

interface PackageSizeCardProps {
  form: FormInstance; // Accept form instance from parent
  onPackageSizeChange: (size: string) => void; // New prop to handle size change
  packageSizeProp?: string;
  orderStatus?: string;
  orderOptions?: OrderOption[];
  clueVerificationLink?: string;
  idCheck?: boolean;
  hasFragileProduct?: boolean;
}

const PackageSizeEditCard: React.FC<PackageSizeCardProps> = ({
  form,
  onPackageSizeChange,
  packageSizeProp,
  orderStatus,
  orderOptions,
  idCheck,
  clueVerificationLink,
  hasFragileProduct = false,
}) => {
  const [selectedSize, setSelectedSize] = useState<PackageSizeFormValues>('S');
  const [isFragileChecked, setIsFragileChecked] = useState(false);
  const [isTemperatureChecked, setIsTemperatureChecked] = useState(false);
  const [isIdChecked, setIsIdChecked] = useState(false);
  const [isClueVerificationChecked, setIsClueVerificationChecked] = useState(false);
  const [hasIdChecked, setHasIdChecked] = useState(false);
  const [hasClueVerification, setHasClueVerification] = useState(false);
  const { t } = useTranslation();

  const handleFragileChange = (e: CheckboxChangeEvent) => {
    setIsFragileChecked(e.target.checked);
  };

  const handleTemperatureChange = (e: CheckboxChangeEvent) => {
    setIsTemperatureChecked(e.target.checked);
  };

  // TEMP-5: the flag is editable only while the order is New or Scheduled
  // (pattern: OrderTypeCard status gate). No orderStatus (create flow) = enabled.
  const isTemperatureLocked =
    !!orderStatus && orderStatus !== 'new' && orderStatus !== 'scheduled';

  const handleIdChange = (e: CheckboxChangeEvent) => {
    setIsIdChecked(e.target.checked);
  };

  const handleClueVerificationChange = (e: CheckboxChangeEvent) => {
    setIsClueVerificationChecked(e.target.checked);
  };

  // Sync the selected size when the form initializes or updates
  useEffect(() => {
    const initialSize: PackageSizeFormValues | undefined =
      packageSizeProp || form.getFieldValue('packageSize');

    if (initialSize) {
      setSelectedSize(initialSize);
      form.setFieldsValue({ packageSize: initialSize });
    }
  }, [form, packageSizeProp]);

  useEffect(() => {
    const idCheckedValue = orderOptions?.findIndex((opt) => opt.type === OrderOptionEnum.ID_CHECK);
    const clueVerificationValue = orderOptions?.findIndex(
      (opt) => opt.type === OrderOptionEnum.CLUE_VERIFICATION,
    );

    // Show verification options only in bulk import modal (orderOptions is undefined).
    // Hide in create order (empty array) and edit order (populated array).
    setHasIdChecked(orderOptions === undefined);
    setHasClueVerification(orderOptions === undefined);

    if (idCheckedValue !== undefined || idCheck) {
      setIsIdChecked(idCheckedValue !== -1 || !!idCheck);
    }
    if (clueVerificationValue !== undefined || clueVerificationLink) {
      setIsClueVerificationChecked(clueVerificationValue !== -1 || !!clueVerificationLink);
    }
  }, [orderOptions, idCheck, clueVerificationLink]);

  return (
    <Card>
      <Heading
        title={t('package_size')}
        fontSize='text-xs'
        fontWeight='font-bold'
        color='text-sd-muted-foreground'
        extraClassName='uppercase tracking-wider mb-4'
      />
      <Form.Item name='packageSize' noStyle>
        <PackageSizeInput
          form={form}
          onPackageSizeChange={onPackageSizeChange}
          packageSizeOptions={packageSizeOptions}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          t={t}
        />
      </Form.Item>
      <div className='border-t border-sd-border mt-4 pt-4 space-y-3'>
        <FormCheckbox
          name='isFragile'
          checked={isFragileChecked}
          onChange={handleFragileChange}
          label={t('package_content_is_fragile')}
          form={form}
          show={true}
          icon={<FragileIcon width={16} height={16} />}
          disabled={hasFragileProduct}
        />
        <FormCheckbox
          name='temperatureControl'
          checked={isTemperatureChecked}
          onChange={handleTemperatureChange}
          label={String(t('temperature_control'))}
          form={form}
          show={true}
          icon={
            // TEMP-12 — icon parity with the fragile row above: same 16px
            // slot in the FormCheckbox label, Snowflake = the cold-chain
            // glyph used across list/summary/details (stroke inherits the
            // label's text-sd-foreground via currentColor).
            <span data-testid='temperature-control-checkbox-icon' className='inline-flex shrink-0'>
              <Snowflake className='h-4 w-4' strokeWidth={2} />
            </span>
          }
          disabled={isTemperatureLocked}
        />
        {isTemperatureLocked ? (
          <p className='text-xs leading-4 text-sd-muted-foreground ps-6 mb-0'>
            {String(t('temperature_control_locked_hint'))}
          </p>
        ) : null}
        <FormCheckbox
          name='isIdChecked'
          checked={isIdChecked}
          onChange={handleIdChange}
          label={t('id_checked')}
          form={form}
          show={hasIdChecked}
        />

        <FormCheckbox
          name='isClueVerification'
          checked={isClueVerificationChecked}
          onChange={handleClueVerificationChange}
          label={t('clue_verification')}
          form={form}
          show={hasClueVerification}
        />
        {hasClueVerification && (
          <Form.Item
            name='clueLink'
            className='w-full mb-0'
            rules={[
              ...(isClueVerificationChecked
                ? [{ required: true, message: t('this_field_is_required') }]
                : []),
              {
                type: 'url' as const,
                message: t('please_enter_a_valid_url'),
              },
            ]}
          >
            <Input
              placeholder={t('verification_url_optional')}
              className='border w-full border-solid border-sd-input bg-sd-input-bg h-[40px] rounded-md hover:border-sd-input focus:border-sd-input focus:shadow-none'
            />
          </Form.Item>
        )}
      </div>
    </Card>
  );
};

export default PackageSizeEditCard;
